import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PRO_EMAIL_KEY = "streak_pro_email";

// Check if test mode is active via URL query param
function isTestModeActive(testCode) {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  const urlTestCode = params.get("paywalltest");
  return urlTestCode && urlTestCode === testCode;
}

export function usePro() {
  const [isPro, setIsPro] = useState(false);
  const [proEmail, setProEmail] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [paywallEnabled, setPaywallEnabled] = useState(false);
  const [paywallTestMode, setPaywallTestMode] = useState(false);
  const [testModeActive, setTestModeActive] = useState(false);
  const [tokenPresent, setTokenPresent] = useState(false);

  // Load config and check Pro status on mount (auth via httpOnly cookie)
  useEffect(() => {
    const init = async () => {
      try {
        const configResponse = await axios.get(`${API}/pro/config`);
        const config = configResponse.data;

        setPaywallEnabled(config.paywallEnabled);
        setPaywallTestMode(config.paywallTestMode);

        const isTestActive = config.paywallTestMode && isTestModeActive(config.paywallTestCode);
        setTestModeActive(isTestActive);

        // Cookie is sent automatically; /pro/me reads it server-side
        const response = await axios.get(`${API}/pro/me`);
        if (response.data.isPro) {
          setIsPro(true);
          setTokenPresent(true);
          setProEmail(response.data.email || localStorage.getItem(PRO_EMAIL_KEY));
        }
      } catch (error) {
        console.error("Error initializing Pro status:", error);
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, []);

  // Deprecated: token now lives in an httpOnly cookie and is not readable by JS
  const getProToken = useCallback(() => null, []);

  const unlockPro = useCallback(async (email, demoCode = null) => {
    try {
      const response = await axios.post(`${API}/pro/unlock`, {
        email,
        demoCode
      });

      if (response.data.isPro) {
        // Non-sensitive display cache only (no token stored)
        localStorage.setItem(PRO_EMAIL_KEY, email);
        setIsPro(true);
        setProEmail(email);
        setTokenPresent(true);
        return { success: true, method: response.data.method };
      }

      return { success: false, message: response.data.message || "No active subscription found" };
    } catch (error) {
      console.error("Error unlocking Pro:", error);
      return { success: false, message: "Failed to verify subscription" };
    }
  }, []);

  const clearPro = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (error) {
      console.error("Error clearing Pro:", error);
    }
    localStorage.removeItem(PRO_EMAIL_KEY);
    setIsPro(false);
    setProEmail(null);
    setTokenPresent(false);
  }, []);

  const effectivePaywallEnabled = paywallEnabled || testModeActive;

  const requiresPro = useCallback((featureName) => {
    if (!effectivePaywallEnabled) return false;
    if (isPro) return false;
    return true;
  }, [effectivePaywallEnabled, isPro]);

  return {
    isPro,
    proEmail,
    isLoading,
    paywallEnabled,
    paywallTestMode,
    testModeActive,
    effectivePaywallEnabled,
    tokenPresent,
    getProToken,
    unlockPro,
    clearPro,
    requiresPro
  };
}

export default usePro;
